import React, { useRef, useState } from "react";
import { HomeOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Input, Space, Table } from "antd";
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./history_data_item_location.css";
import axios from "axios";
import useSWR from "swr";

interface DataType {
    key: React.Key;
    item_code: string;
    item_name: string;
    rack_location_code: string;
    rack_location_name: string;
    rack_location_code_change: string;
    rack_location_name_change: string;
}

type DataIndex = keyof DataType;
const fetcher = (url: string) => axios.get(url).then(res => res.data);
const HistoryDataItemLocation: React.FC = () => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    const { data, error } = useSWR<DataType[]>(`${process.env.REACT_APP_BACKEND_URL}/api/item-location-history`, fetcher);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<DataType> = [
        {
            title: 'Item Code',
            dataIndex: 'item_code',
            key: 'item_code',
            sorter: (a, b) => a.item_code.localeCompare(b.item_code),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('item_code'),
        },
        {
            title: 'Item Name',
            dataIndex: 'item_name',
            key: 'item_name',
            sorter: (a, b) => a.item_name.localeCompare(b.item_name),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('item_name'),
        },
        {
            title: 'Rack Location Code',
            dataIndex: 'rack_location_code',
            key: 'rack_location_code',
            sorter: (a, b) => a.rack_location_code.localeCompare(b.rack_location_code),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('rack_location_code'),
        },
        {
            title: 'Rack Location Name',
            dataIndex: 'rack_location_name',
            key: 'rack_location_name',
            sorter: (a, b) => a.rack_location_name.localeCompare(b.rack_location_name),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('rack_location_name'),
        },
        {
            title: 'Rack Location Code (Change)',
            dataIndex: 'rack_location_code_change',
            key: 'rack_location_code_change',
            sorter: (a, b) => a.rack_location_code_change.localeCompare(b.rack_location_code_change),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('rack_location_code_change'),
        },
        {
            title: 'Rack Location Name (Change)',
            dataIndex: 'rack_location_name_change',
            key: 'rack_location_name_change',
            sorter: (a, b) => a.rack_location_name_change.localeCompare(b.rack_location_name_change),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('rack_location_name_change'),
        },
    ];

    if (error) return <div>Failed to load data.</div>;

    return (

        <div id="ilqt" style={transformStyle}>
            <div className="gjs-grid-row" id="izrb">
                <div className="gjs-grid-column" id="iv4q">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                title: (
                                    <>
                                        <span>History Transaction Item Location</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/item_location",
                                title: "Item Location",
                            },
                        ]}
                    />
                </div>
            </div>
            <div className="gjs-grid-row" id="i8x04">
                <div className="gjs-grid-column" id="ii9yx">
                    <Table<DataType> columns={columns} dataSource={data} loading={!data} />
                </div>
            </div>
        </div>
    );
};

export default HistoryDataItemLocation;